/**
 * Text rendering utilities shared across RightMessage applications
 */

export interface Phrase {
  id: string
  fallbackText: string
}

export interface RenderTextOptions {
  phrases?: Phrase[]
  truncationLimit?: number
}

const MOJIBAKE_PATTERN = /(?:Ã[\u0080-\u00BF]|Â[\u0080-\u00BF]|â[\u0080-\u00BF€])/u

const WINDOWS_1252_BYTES: Record<string, number> = {
  '€': 0x80,
  '‚': 0x82,
  ƒ: 0x83,
  '„': 0x84,
  '…': 0x85,
  '†': 0x86,
  '‡': 0x87,
  ˆ: 0x88,
  '‰': 0x89,
  Š: 0x8a,
  '‹': 0x8b,
  Œ: 0x8c,
  Ž: 0x8e,
  '‘': 0x91,
  '’': 0x92,
  '“': 0x93,
  '”': 0x94,
  '•': 0x95,
  '–': 0x96,
  '—': 0x97,
  '˜': 0x98,
  '™': 0x99,
  š: 0x9a,
  '›': 0x9b,
  œ: 0x9c,
  ž: 0x9e,
  Ÿ: 0x9f
}

function toWindows1252Bytes(text: string): Uint8Array | null {
  const bytes = new Uint8Array(text.length)

  for (let index = 0; index < text.length; index++) {
    const codePoint = text.charCodeAt(index)
    const specialByte = WINDOWS_1252_BYTES[text.charAt(index)]

    if (specialByte !== undefined) {
      bytes[index] = specialByte
    } else if (codePoint <= 0xff) {
      bytes[index] = codePoint
    } else {
      return null
    }
  }

  return bytes
}

/**
 * Repair text whose UTF-8 bytes were decoded as Windows-1252 or Latin-1 before storage.
 */
export function repairMojibake(text: string): string {
  if (!text || !MOJIBAKE_PATTERN.test(text)) {
    return text
  }

  const bytes = toWindows1252Bytes(text)
  if (!bytes) {
    return text
  }

  try {
    const repaired = new TextDecoder('utf-8', { fatal: true }).decode(bytes)
    const roundTrip = new TextEncoder().encode(repaired)

    return roundTrip.length === bytes.length && roundTrip.every((byte, index) => byte === bytes[index])
      ? repaired
      : text
  } catch {
    return text
  }
}

/**
 * Render text with Liquid templating and phrase replacement
 * This is a simplified version that doesn't require Liquid.js
 * For full Liquid support, the consuming app should pre-process the text
 *
 * @param text - The text to render
 * @param options - Rendering options
 * @returns The rendered text
 */
export function renderText(text: string, options: RenderTextOptions = {}): string {
  const { phrases = [], truncationLimit = 100 } = options

  if (!text) {
    return ''
  }

  // Replace phrase placeholders with fallback text
  let processedText = processLiquid(text, phrases)

  // Note: Full Liquid processing should be done by the consuming app
  // This is just for basic phrase replacement

  // Convert HTML to plain text
  const plainText = stripHtml(processedText)

  // Apply truncation if needed
  return plainText.length > truncationLimit ? plainText.substring(0, truncationLimit) + '...' : plainText
}

/**
 * Process basic Liquid-like phrase replacements
 * @param text - The text to process
 * @param phrases - Array of phrases to replace
 * @returns Text with phrases replaced
 */
export function processLiquid(text: string, phrases: Phrase[]): string {
  return text.replace(/\{\{\s*phrases\.(\w+)\s*\}\}/g, (match, phraseId) => {
    // Ensure phrases is an array before calling find
    if (!Array.isArray(phrases)) {
      return match
    }
    const phrase = phrases.find((p) => p.id === phraseId)
    return phrase ? phrase.fallbackText : match
  })
}

export function plainTextHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/**
 * Strip HTML tags from text
 * @param html - HTML string to strip
 * @returns Plain text without HTML tags
 */
export function stripHtml(html: string | null): string {
  if (!html) {
    return ''
  }

  // Check if we're in a browser environment
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('DIV')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }

  // Fallback for SSR: simple regex-based HTML stripping
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '') // Remove style tags
    .replace(/<[^>]+>/g, '') // Remove HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp;
    .replace(/&amp;/g, '&') // Replace &amp;
    .replace(/&lt;/g, '<') // Replace &lt;
    .replace(/&gt;/g, '>') // Replace &gt;
    .replace(/&quot;/g, '"') // Replace &quot;
    .replace(/&#39;/g, "'") // Replace &#39;
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim()
}

/**
 * Capitalize the first letter of a string
 */
export function capitalize(x: string): string {
  if (x && x.length) {
    const firstLetterIndex = x.search(/[a-zA-Z]/)
    if (firstLetterIndex !== -1) {
      return x.slice(0, firstLetterIndex) + x.charAt(firstLetterIndex).toUpperCase() + x.slice(firstLetterIndex + 1)
    }
  }
  return x
}

/**
 * Convert an array of strings to a sentence with commas and 'and'
 */
export function toSentence(x: string[]): string {
  if (x.length === 1) {
    const [only] = x
    if (only === undefined) {
      throw new TypeError('toSentence received a missing element')
    }
    return only
  }
  return x.slice(0, -1).join(', ') + ' and ' + x[x.length - 1]
}
